import {
  $Prescriptions,
  $PrescriptionsStates,
  resetPrescriptions,
  fetchPrescriptions,
} from "@store/prescription-store";
import { $DepartureScheduleFilters } from "@store/departure-schedule-store";
import { $User } from "@store/user-store";
import { UserDataT } from "@interfaces/user";
import { useCompaniesQuery } from "@lib/company";
import { DepartureScheduleFiltersCompany } from "@interfaces/departure-schedule";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { ruRU } from "@mui/x-date-pickers/locales";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { DatePicker } from "@mui/x-date-pickers";
import { TextField, Autocomplete, MenuItem } from "@mui/material";
import Dropzone from "@ui/dropzone";
import clsx from "clsx";
import style from "@scss/modals/prescriptions/choosing-executor.module.scss";
import { useModal } from "@modals/index";
import useStyles from "@ui/material-ui-styles";
import { useState, useEffect } from "react";
import { useUnit } from "effector-react";
import moment, { Moment } from "moment";
import { getCompanyLogo } from "@utils/getCompanyLogo";

const DepartureScheduleHistoryCreate: React.FC = () => {
  const { companies } = useCompaniesQuery();
  const { modalComponent, modalData, close } = useModal();
  const { rows } = useUnit($Prescriptions);
  const user = useUnit($User) as UserDataT;
  const filters = useUnit($DepartureScheduleFilters);
  const { isFetched, isLoading, error } = useUnit($PrescriptionsStates);

  const [value, setValue] = useState(moment());
  const [companyId, setCompanyId] = useState<number>();
  const [prescription, setPrescription] = useState<number>(modalData.prescriptionId ? modalData.prescriptionId : undefined);
  const [comment, setComment] = useState('');
  const [dateDaparture, setDateDaparture] = useState(moment());
  const [files, setFiles] = useState<File[]>([]);
  const [disabled, setDisabled] = useState(modalData.type === "full" ? true : false);

  const classes = useStyles();

  useEffect(() => {
    if (modalData.date) {
      setValue(moment(modalData.date));
    }
    if (modalData.companyId) {
      setCompanyId(modalData.companyId);
    }
    if (modalData.comment) {
      setComment(modalData.comment);
    }
    if (modalData.dateDaparture) {
      setDateDaparture(moment(modalData.dateDaparture));
    }

    resetPrescriptions();
    fetchPrescriptions({
      companyId: modalData.companyId,
      user: user?.id!,
      offset: 0,
      limit: 100000
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalData.date, modalData.companyId, modalData.comment, modalData.dateDaparture]);

  const getCompany = () => {
    const company = companies ? companies.find(e => e.id === companyId) : undefined;
    return company !== undefined ? company : undefined;
  }

  return (
    <div
      key={modalComponent.key}
      className={clsx(style.send_prescription_modal)}
    >
      <p className="modal_title">{modalData.title}</p>
      <div className="underline" />
      <div className={clsx("modal_content", classes.root, style.content)}>
        {modalData.isCompany && (
          <Autocomplete
            fullWidth
            size="small"
            options={filters.company!}
            getOptionLabel={(option) => option.name}
            getOptionKey={(option) => option.id}
            value={getCompany()}
            noOptionsText={"Нет совпадений"}
            onChange={(e: any, value: DepartureScheduleFiltersCompany | null) => {
              if (value) setCompanyId(value.id);
            }}
            slotProps={{
              listbox: {
                sx: {
                  padding: "0",
                },
              },
              popper: {
                popperOptions: {
                  placement: "bottom-start",
                },
                sx: {
                  width: "auto !important",
                  maxWidth: "30vw",
                },
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="filled"
                label="Компания"
                placeholder="Введите значение"
              />
            )}
          />
        )}
        {modalData.type === "planned" && (
          <LocalizationProvider
            dateAdapter={AdapterMoment}
            adapterLocale={"ru"}
            localeText={ruRU.components.MuiLocalizationProvider.defaultProps.localeText}
          >
            <DateCalendar
              value={value}
              onChange={(value: Moment) => setValue(value)}
              views={["day"]}
            />
          </LocalizationProvider>
        )}
        {modalData.type === "full" && isFetched && (
          <>
            <LocalizationProvider
              dateAdapter={AdapterMoment}
              adapterLocale={"ru"}
              localeText={ruRU.components.MuiLocalizationProvider.defaultProps.localeText}
            >
              <DatePicker
                value={dateDaparture}
                onChange={(value) => setDateDaparture(value!)}
                sx={{ width: "100%" }}
                format="DD.MM.YYYY"
                autoFocus={false}
                slotProps={{
                  actionBar: { actions: ["cancel", "accept"] },
                  textField: {
                    variant: "filled",
                  },
                }}
                label="Дата выезда"
                localeText={{
                  toolbarTitle: "",
                  cancelButtonLabel: "Отменить",
                  okButtonLabel: "ОК",
                }}
              />
            </LocalizationProvider>
            <TextField
              label="Предписание"
              name="prescription"
              variant="filled"
              required
              select
              value={prescription}
              onChange={(e) => {
                setPrescription(+e.target.value)
                if(dateDaparture){
                  setDisabled(false)
                }
              }}
            >
            {rows && rows.map((prescription) => (
              <MenuItem value={prescription.id}>Предписание #{prescription.id}/{prescription.companyId} ({moment(prescription.createdAt).format('DD.MM.YYг.')})</MenuItem>
            ))}
            </TextField>
            <TextField 
              multiline
              rows={5}
              label="Примечание"
              variant="filled"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
            <Dropzone
              files={files}
              maxFiles={1}
              withPreviews
              onUpload={(filesUpload: File[]) => setFiles((prev) => [...prev, ...filesUpload])}
              onDeleteFile={(file: File) => setFiles((files) => files.filter((_file) => _file.name !== file.name))}
            />
          </>
        )}
        <button
          type="submit"
          className={clsx("modal_btn", style.close_button)}
          disabled={disabled}
          onClick={(e) => {
            (e.target as HTMLButtonElement).disabled = true;
            close();
            return modalComponent.onConfirm
              ? modalComponent.onConfirm(value, companyId, prescription, dateDaparture, comment, files)
              : null;
          }}
        >
          {modalComponent.btnText}
        </button>
      </div>
    </div>
  );
};

export default DepartureScheduleHistoryCreate;
